import React, { FunctionComponent } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import Typography from '@material-ui/core/Typography';
import { Substance } from '../../shared/models/Substance';
import { useTranslation } from 'react-i18next';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import {
    AppBar,
    Box,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemText,
    Paper,
    Slide,
    Toolbar,
} from '@material-ui/core';
import Snackbar from '@material-ui/core/Snackbar';
import { parseFormula } from '../SubstancesTable/SubstancesTable';
import { TransitionProps } from '@material-ui/core/transitions/transition';
import { InfoTooltip } from '../InfoTooltip/InfoTooltip';

export const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        appBar: {
            position: 'relative',
        },
        title: {
            marginLeft: theme.spacing(2),
            flex: 1,
        },
        snackBar: {
            maxWidth: '100%',
            width: '30rem',
        },
        listItem: {
            margin: '20px',
        },
        list: {
            padding: 0,
        },
        labelBox: {
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
        },
        paperWidthSm: {
            maxWidth: '800px',
        },
    }),
);

export const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children?: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface StyledValueParams {
    value: string;
    handleCopyClick: () => any;
    formula?: 'formula' | 'kbName' | false;
}

interface LabelWithInfoParams {
    label: string;
    info: string;
}

const LabelWithInfo: FunctionComponent<LabelWithInfoParams> = ({
    label,
    info,
}) => {
    return (
        <>
            <InfoTooltip title={info} color="primary" />
            {label}
        </>
    );
};

const StyledValue: FunctionComponent<StyledValueParams> = ({
    children,
    value,
    handleCopyClick,
    formula = false,
}) => {
    const classes = useStyles();
    return (
        <ListItem
            button
            onClick={() => {
                if (window.isSecureContext) {
                    navigator.clipboard.writeText(value);
                    handleCopyClick();
                }
            }}
        >
            <ListItemText>
                <Grid container justify="center" spacing={3}>
                    <Grid item xs={6}>
                        <Typography component="div">
                            <Box className={classes.labelBox}>{children}</Box>
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        {formula === 'formula' ? (
                            <Box
                                dangerouslySetInnerHTML={{
                                    __html: parseFormula(value),
                                }}
                            />
                        ) : formula === 'kbName' ? (
                            <Box
                                dangerouslySetInnerHTML={{
                                    __html: parseFormula(value, true),
                                }}
                            />
                        ) : (
                            <Box>{value}</Box>
                        )}
                    </Grid>
                </Grid>
            </ListItemText>
        </ListItem>
    );
};

function Alert(props: AlertProps) {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
}

interface SubstanceModalParams {
    substance: Substance;
}
interface SubstanceContianerParams {
    substance: Substance;
    onClick: () => any;
}

export const SubstanceContainer: FunctionComponent<SubstanceContianerParams> = ({
    substance,
    onClick,
}) => {
    const [t] = useTranslation();
    const classes = useStyles();
    return (
        <Paper elevation={3} className={classes.listItem}>
            <List className={classes.list}>
                <StyledValue
                    value={substance.casNumber}
                    handleCopyClick={onClick}
                >
                    <LabelWithInfo label={t('CAS')} info={t('CAS info')} />
                </StyledValue>
                <Divider />
                <StyledValue value={substance.name} handleCopyClick={onClick}>
                    {t('Name')}
                </StyledValue>
                <Divider />
                {substance.percentage && (
                    <>
                        <StyledValue
                            value={substance.percentage.toLocaleString() + ' %'}
                            handleCopyClick={onClick}
                        >
                            {t('Percentage')}
                        </StyledValue>
                        <Divider />
                    </>
                )}
                <StyledValue
                    value={substance.otherNames.join(', ')}
                    handleCopyClick={onClick}
                >
                    {t('Other names')}
                </StyledValue>
                <Divider />
                <StyledValue
                    value={substance.formula}
                    formula="formula"
                    handleCopyClick={onClick}
                >
                    {t('Formula')}
                </StyledValue>
                <Divider />
                <StyledValue
                    value={substance.kbNumber}
                    handleCopyClick={onClick}
                >
                    <LabelWithInfo label={t('USZE')} info={t('USZE info')} />
                </StyledValue>
                <Divider />
                <StyledValue
                    value={substance.kbName}
                    formula="kbName"
                    handleCopyClick={onClick}
                >
                    <LabelWithInfo
                        label={t('KB name')}
                        info={t('KB name info')}
                    />
                </StyledValue>
                <Divider />
                <StyledValue
                    value={substance.chargingSystemNumber}
                    handleCopyClick={onClick}
                >
                    <LabelWithInfo
                        label={t('Charging system number')}
                        info={t('Charging system number info')}
                    />
                </StyledValue>
                <Divider />
                <StyledValue
                    value={substance.chargingSystemName}
                    handleCopyClick={onClick}
                >
                    <LabelWithInfo
                        label={t('Charging system name')}
                        info={t('Charging system name info')}
                    />
                </StyledValue>
                <Divider />
                <StyledValue
                    value={substance.annualRate}
                    handleCopyClick={onClick}
                >
                    <LabelWithInfo
                        label={window.appConfig.ANNUAL_LABEL}
                        info={window.appConfig.ANNUAL_TOOLTIP}
                    />
                </StyledValue>
            </List>
        </Paper>
    );
};

export const SubstanceModal: FunctionComponent<SubstanceModalParams> = ({
    substance,
}) => {
    const [t] = useTranslation();

    const classes = useStyles();
    const [open, setOpen] = React.useState(false);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const [snackbarState, setSnackbarState] = React.useState(false);

    const handleCopyClick = () => {
        setSnackbarState(true);
    };

    const handleCopyClose = (event?: React.SyntheticEvent, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarState(false);
    };

    return (
        <div>
            <Button
                color="primary"
                onClick={handleClickOpen}
                variant="contained"
            >
                {t('Select')}
            </Button>
            <Dialog
                // fullScreen
                open={open}
                onClose={handleClose}
                TransitionComponent={Transition}
                fullWidth
                classes={{
                    paperWidthSm: classes.paperWidthSm,
                }}
            >
                <AppBar className={classes.appBar}>
                    <Toolbar>
                        <Typography variant="h6" className={classes.title}>
                            {substance.name}
                        </Typography>
                        <Button autoFocus color="inherit" onClick={handleClose}>
                            {t('Close')}
                        </Button>
                    </Toolbar>
                </AppBar>
                <SubstanceContainer
                    substance={substance}
                    onClick={handleCopyClick}
                />
                <Snackbar
                    open={snackbarState}
                    autoHideDuration={700}
                    onClose={handleCopyClose}
                >
                    <Alert
                        className={classes.snackBar}
                        onClose={handleCopyClose}
                        severity="success"
                    >
                        {t('Copied!')}
                    </Alert>
                </Snackbar>
            </Dialog>
        </div>
    );
};
